<template>
  <!-- Повноекранний анімований гайд встановлення PWA на iPhone -->
  <Transition name="guide-fade">
    <div v-if="isGuideOpen" class="ios-guide" @click.self="close">
      <div class="ios-guide__backdrop"></div>

      <div class="ios-guide__sheet" role="dialog" aria-modal="true">
        <!-- Заголовок -->
        <div class="ios-guide__header">
          <h2 class="ios-guide__title">{{ t('pwa.iosGuide.title') }}</h2>
          <button class="ios-guide__close" @click="close" :aria-label="t('common.close')">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <!-- Попередження для iOS не-Safari: установка неможлива в поточному браузері -->
        <div v-if="isNonSafariIos" class="ios-guide__warn">
          <div class="ios-guide__warn-head">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 8v4M12 16h.01"/>
            </svg>
            <span>{{ t('pwa.iosGuide.notSafariTitle') }}</span>
          </div>
          <p class="ios-guide__warn-text">{{ t('pwa.iosGuide.notSafariDesc') }}</p>
          <button class="ios-guide__warn-btn" @click="copyUrl">
            {{ copied ? t('pwa.install.copied') : t('pwa.install.copyUrl') }}
          </button>
        </div>

        <!-- Підказка кроку (синхронізована з фазою анімації) -->
        <div class="ios-guide__step-label">
          <span class="step-num">{{ currentStep }}</span>
          <span class="step-text">{{ t(`pwa.iosGuide.step${currentStep}`) }}</span>
        </div>

        <!-- Сцена анімації: імітація екрана iPhone -->
        <div class="ios-guide__stage">
          <div class="iphone">
            <!-- Динамічний острівець (імітація) -->
            <div class="iphone__dynamic-island"></div>

            <!-- Область контенту "Safari" -->
            <div class="iphone__content">
              <!-- Заглушка контенту сайту -->
              <div class="site-mock">
                <div class="site-mock__bar"></div>
                <div class="site-mock__block"></div>
                <div class="site-mock__block site-mock__block--short"></div>
                <div class="site-mock__block"></div>
              </div>

              <!-- Action sheet Safari (з'являється на кроці 2) -->
              <div class="share-sheet" :class="{ 'share-sheet--visible': phase >= 2 }">
                <div class="share-sheet__handle"></div>
                <div class="share-sheet__header">
                  <div class="share-sheet__avatar"></div>
                  <div class="share-sheet__meta">
                    <div class="share-sheet__title-line"></div>
                    <div class="share-sheet__url-line"></div>
                  </div>
                </div>
                <!-- Ряд іконок дій -->
                <div class="share-sheet__row">
                  <div class="share-icon">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M20 6H4v12h16V6zm-2 10H6V8h12v8z"/></svg>
                  </div>
                  <div class="share-icon">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M12 2a10 10 0 100 20 10 10 0 000-20z"/></svg>
                  </div>
                  <div class="share-icon">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M4 4h16v4H4zM4 10h16v4H4zM4 16h16v4H4z"/></svg>
                  </div>
                </div>
                <!-- Пункт "На екран Початок" (підсвічується на кроці 3) -->
                <div class="share-sheet__item" :class="{ 'share-sheet__item--highlight': phase >= 3 }">
                  <span class="share-sheet__item-text">{{ t('pwa.iosGuide.addToHome') }}</span>
                  <span class="share-sheet__item-icon">
                    <!-- SF-подібна "plus.square" -->
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
                      <rect x="3.5" y="3.5" width="17" height="17" rx="4"/>
                      <path d="M12 8v8M8 12h8"/>
                    </svg>
                  </span>
                  <!-- Tap-ripple на кроці 3 -->
                  <span v-if="phase === 3" class="tap-ripple"></span>
                </div>
                <div class="share-sheet__item share-sheet__item--dim">
                  <span class="share-sheet__item-text">{{ t('pwa.iosGuide.bookmark') }}</span>
                </div>
              </div>
            </div>

            <!-- Нижній toolbar Safari з кнопкою "Поділитися" -->
            <div class="safari-bar">
              <div class="safari-bar__btn"><span class="chev chev--left"></span></div>
              <div class="safari-bar__btn"><span class="chev chev--right"></span></div>
              <!-- Головна ціль кроку 1: кнопка Share -->
              <div class="safari-bar__btn safari-bar__btn--share" :class="{ 'safari-bar__btn--highlight': phase === 1 }">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 3v12" />
                  <path d="M8 7l4-4 4 4" />
                  <path d="M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
                <!-- Стрілка-пульс вказує на кнопку -->
                <div v-if="phase === 1" class="pointer-arrow" aria-hidden="true">
                  <svg viewBox="0 0 24 40" width="24" height="40" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 4v28" />
                    <path d="M6 26l6 6 6-6" />
                  </svg>
                </div>
              </div>
              <div class="safari-bar__btn"><span class="book-icon"></span></div>
              <div class="safari-bar__btn"><span class="tabs-icon"></span></div>
            </div>
          </div>
        </div>

        <!-- Прогрес-точки -->
        <div class="ios-guide__dots">
          <span v-for="n in 3" :key="n" class="dot" :class="{ 'dot--active': phase === n }"></span>
        </div>

        <!-- Футер: пояснення + кнопки -->
        <div class="ios-guide__footer">
          <p class="ios-guide__hint">{{ t('pwa.iosGuide.hint') }}</p>
          <button class="ios-guide__btn" @click="close">{{ t('common.close') }}</button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { t } from '../services/i18nService'
import { useIosInstallGuide, isSafariOnIOS, isIOS } from '../composables/useIosInstallGuide'

const { isGuideOpen, closeGuide } = useIosInstallGuide()

// Якщо гайд відкрили з iOS, але не в Safari — установка неможлива з цього браузера
const isNonSafariIos = computed(() => isIOS() && !isSafariOnIOS())
const copied = ref(false)
const copyUrl = async () => {
  try {
    await navigator.clipboard.writeText(window.location.href)
  } catch {
    const ta = document.createElement('textarea')
    ta.value = window.location.href
    document.body.appendChild(ta)
    ta.select()
    try { document.execCommand('copy') } catch {}
    document.body.removeChild(ta)
  }
  copied.value = true
  setTimeout(() => { copied.value = false }, 1800)
}

// Фаза анімації: 1 = вказівка на Share, 2 = з'являється action sheet, 3 = підсвічується пункт Add to Home
const phase = ref(1)
const currentStep = ref(1)

let timerId = null

// Тривалість кожної фази (мс)
const PHASE_DURATIONS = [2200, 2000, 2400]

const advance = () => {
  phase.value = phase.value >= 3 ? 1 : phase.value + 1
  currentStep.value = phase.value
  scheduleNext()
}

const scheduleNext = () => {
  clearTimeout(timerId)
  timerId = setTimeout(advance, PHASE_DURATIONS[phase.value - 1])
}

const startCycle = () => {
  phase.value = 1
  currentStep.value = 1
  scheduleNext()
}

const stopCycle = () => {
  clearTimeout(timerId)
  timerId = null
}

const close = () => {
  closeGuide()
}

// Запуск/зупинка циклу при відкритті/закритті
watch(isGuideOpen, (open) => {
  if (open) startCycle()
  else stopCycle()
}, { immediate: true })

onBeforeUnmount(stopCycle)
</script>

<style scoped>
/* ===== Основна оболонка ===== */
.ios-guide {
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.ios-guide__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(10, 16, 28, 0.72);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.ios-guide__sheet {
  position: relative;
  width: 100%;
  max-width: 380px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  background: var(--bg-card);
  border-radius: 22px;
  padding: 16px 16px 20px;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.32);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ===== Заголовок ===== */
.ios-guide__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.ios-guide__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-heading);
}

.ios-guide__close {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.ios-guide__close:hover { background: var(--border-default); }

/* ===== Попередження "не Safari" ===== */
.ios-guide__warn {
  background: var(--warning-light, #fff4e0);
  border: 1px solid var(--warning, #ff9500);
  border-radius: 12px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ios-guide__warn-head {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--warning-text, #8a4b00);
  font-weight: 600;
  font-size: 0.88rem;
}
.ios-guide__warn-text {
  margin: 0;
  font-size: 0.8rem;
  color: var(--text-primary);
  line-height: 1.4;
}
.ios-guide__warn-btn {
  align-self: flex-start;
  padding: 6px 12px;
  border-radius: 8px;
  border: none;
  background: var(--warning, #ff9500);
  color: #fff;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
}

/* ===== Підпис кроку ===== */
.ios-guide__step-label {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: var(--primary-bg);
  border-radius: 12px;
  font-size: 0.9rem;
  color: var(--primary-text);
  font-weight: 500;
  min-height: 44px;
}
.step-num {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--primary);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ===== Сцена (iPhone) ===== */
.ios-guide__stage {
  display: flex;
  justify-content: center;
  padding: 8px 0;
}

.iphone {
  position: relative;
  width: 230px;
  height: 420px;
  background: #0f1115;
  border-radius: 36px;
  padding: 8px;
  box-shadow:
    0 0 0 3px #1a1d24,
    0 12px 28px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
}

.iphone__dynamic-island {
  position: absolute;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  width: 72px;
  height: 20px;
  background: #000;
  border-radius: 12px;
  z-index: 2;
}

.iphone__content {
  position: relative;
  flex: 1;
  background: linear-gradient(180deg, #f6f7fb 0%, #eceff4 100%);
  border-radius: 26px 26px 6px 6px;
  margin-top: 34px;
  overflow: hidden;
}

/* Заглушка сайту */
.site-mock {
  padding: 18px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.site-mock__bar {
  height: 8px;
  width: 60%;
  background: #cfd4dc;
  border-radius: 4px;
}
.site-mock__block {
  height: 40px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.site-mock__block--short { width: 70%; }

/* ===== Safari toolbar ===== */
.safari-bar {
  display: flex;
  align-items: center;
  justify-content: space-around;
  height: 44px;
  background: rgba(245, 245, 248, 0.95);
  backdrop-filter: blur(6px);
  border-radius: 0 0 26px 26px;
  padding: 0 6px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}
.safari-bar__btn {
  position: relative;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3478f6;
  transition: background 0.25s, transform 0.25s;
}
.safari-bar__btn--highlight {
  background: rgba(52, 120, 246, 0.15);
  animation: btn-pulse 1.2s ease-in-out infinite;
}

@keyframes btn-pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(52, 120, 246, 0.55);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(52, 120, 246, 0);
    transform: scale(1.08);
  }
}

/* Стрілка-вказівник */
.pointer-arrow {
  position: absolute;
  bottom: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  color: #ff9500;
  animation: arrow-bounce 0.9s ease-in-out infinite;
  filter: drop-shadow(0 2px 4px rgba(255, 149, 0, 0.5));
}
@keyframes arrow-bounce {
  0%, 100% { transform: translateX(-50%) translateY(0); }
  50% { transform: translateX(-50%) translateY(-8px); }
}

/* Декоративні іконки в toolbar */
.chev {
  width: 8px;
  height: 8px;
  border-top: 2px solid currentColor;
  border-left: 2px solid currentColor;
}
.chev--left { transform: rotate(-45deg); }
.chev--right { transform: rotate(135deg); }
.book-icon {
  width: 14px;
  height: 16px;
  border: 2px solid currentColor;
  border-radius: 2px;
}
.tabs-icon {
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-radius: 3px;
  box-shadow: -3px 3px 0 -1px currentColor;
}

/* ===== Share sheet ===== */
.share-sheet {
  position: absolute;
  left: 8px;
  right: 8px;
  bottom: 6px;
  background: rgba(250, 250, 252, 0.98);
  backdrop-filter: blur(10px);
  border-radius: 14px;
  padding: 10px;
  transform: translateY(110%);
  opacity: 0;
  transition: transform 0.45s cubic-bezier(0.25, 0.9, 0.35, 1.05), opacity 0.25s;
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.18);
}
.share-sheet--visible {
  transform: translateY(0);
  opacity: 1;
}

.share-sheet__handle {
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: #c7c9d0;
  margin: 0 auto 8px;
}
.share-sheet__header {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 4px 2px 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.share-sheet__avatar {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: linear-gradient(135deg, #3478f6, #7ab0ff);
}
.share-sheet__meta { flex: 1; display: flex; flex-direction: column; gap: 4px; }
.share-sheet__title-line { height: 6px; width: 60%; background: #2b2d33; border-radius: 3px; }
.share-sheet__url-line { height: 5px; width: 80%; background: #bcbec5; border-radius: 3px; }

.share-sheet__row {
  display: flex;
  gap: 8px;
  padding: 10px 2px;
  overflow: hidden;
}
.share-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fff;
  color: #3478f6;
  display: flex;
  align-items: center;
  justify-content: center;
}

.share-sheet__item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 4px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  font-size: 0.75rem;
  color: #1c1c1e;
  font-weight: 500;
  border-radius: 8px;
}
.share-sheet__item--dim { color: #8e8e93; }
.share-sheet__item-icon {
  color: #1c1c1e;
  display: flex;
  align-items: center;
}
.share-sheet__item--highlight {
  background: rgba(52, 120, 246, 0.14);
  animation: item-pulse 1.2s ease-in-out infinite;
}
@keyframes item-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(52, 120, 246, 0.45); }
  50% { box-shadow: 0 0 0 6px rgba(52, 120, 246, 0); }
}

/* Tap ripple */
.tap-ripple {
  position: absolute;
  left: 12px;
  top: 50%;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: rgba(52, 120, 246, 0.6);
  transform: translate(-50%, -50%);
  animation: ripple 1.3s ease-out infinite;
  pointer-events: none;
}
@keyframes ripple {
  0% { transform: translate(-50%, -50%) scale(0.4); opacity: 0.8; }
  100% { transform: translate(-50%, -50%) scale(3); opacity: 0; }
}

/* ===== Прогрес-точки ===== */
.ios-guide__dots {
  display: flex;
  justify-content: center;
  gap: 6px;
}
.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--border-default);
  transition: background 0.2s, transform 0.2s;
}
.dot--active {
  background: var(--primary);
  transform: scale(1.3);
}

/* ===== Футер ===== */
.ios-guide__footer {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: stretch;
}
.ios-guide__hint {
  margin: 0;
  text-align: center;
  font-size: 0.8rem;
  color: var(--text-secondary);
  line-height: 1.4;
}
.ios-guide__btn {
  padding: 11px 18px;
  border-radius: 12px;
  border: none;
  background: var(--primary);
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
}
.ios-guide__btn:hover { background: var(--primary-hover); }

/* ===== Transition ===== */
.guide-fade-enter-active,
.guide-fade-leave-active {
  transition: opacity 0.25s ease;
}
.guide-fade-enter-active .ios-guide__sheet,
.guide-fade-leave-active .ios-guide__sheet {
  transition: transform 0.3s cubic-bezier(0.2, 0.9, 0.3, 1.05);
}
.guide-fade-enter-from,
.guide-fade-leave-to { opacity: 0; }
.guide-fade-enter-from .ios-guide__sheet,
.guide-fade-leave-to .ios-guide__sheet { transform: translateY(16px) scale(0.97); }
</style>
